export const SESSION_STORAGE_KEY = '_s';
export const SESSION_EXPIRY_SAFETY_MARGIN_MS = 10_000;

export interface StoredSession {
  e: number;
  i: string;
  homeUrl?: string;
}

interface SessionStorageArea {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

export interface SessionStorageAreas {
  localStorage: SessionStorageArea;
  sessionStorage: SessionStorageArea;
}

export function isValidSessionExpiresInSeconds(expiresInSeconds: unknown): expiresInSeconds is number {
  if (!(
    typeof expiresInSeconds === 'number' &&
    Number.isSafeInteger(expiresInSeconds) &&
    expiresInSeconds > 0
  )) {
    return false;
  }

  const lifetimeMs = expiresInSeconds * 1_000;
  return Number.isSafeInteger(lifetimeMs) && lifetimeMs > SESSION_EXPIRY_SAFETY_MARGIN_MS;
}

/**
 * Converts the OAuth expires_in lifetime (seconds) into the absolute epoch-ms
 * deadline used by DH session storage. A small margin prevents a token from
 * being treated as usable at the backend expiry boundary.
 */
export function deriveSessionValidToEpochMs(
  expiresInSeconds: unknown,
  nowEpochMs: number = Date.now()
): number | undefined {
  if (
    !isValidSessionExpiresInSeconds(expiresInSeconds) ||
    !Number.isSafeInteger(nowEpochMs) ||
    nowEpochMs < 0
  ) {
    return undefined;
  }

  const lifetimeMs = expiresInSeconds * 1_000;

  const validToEpochMs = nowEpochMs + lifetimeMs - SESSION_EXPIRY_SAFETY_MARGIN_MS;
  return Number.isSafeInteger(validToEpochMs) && validToEpochMs > nowEpochMs
    ? validToEpochMs
    : undefined;
}

export function createStoredSession(accessToken: string, validTo: number): StoredSession {
  return { e: validTo, i: accessToken };
}

export function serializeStoredSession(session: StoredSession): string {
  return JSON.stringify(session);
}

export function clearStoredSession(storageAreas?: SessionStorageAreas): boolean {
  let areas: SessionStorageAreas;

  try {
    areas = storageAreas ?? {
      localStorage: window.localStorage,
      sessionStorage: window.sessionStorage,
    };
  } catch {
    return false;
  }

  let success = true;

  try {
    areas.localStorage.removeItem(SESSION_STORAGE_KEY);
    if (areas.localStorage.getItem(SESSION_STORAGE_KEY) !== null) {
      success = false;
    }
  } catch {
    success = false;
  }

  try {
    areas.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    if (areas.sessionStorage.getItem(SESSION_STORAGE_KEY) !== null) {
      success = false;
    }
  } catch {
    success = false;
  }

  return success;
}

export function storeNonPersistentSession(
  accessToken: string,
  validTo: number,
  storageAreas?: SessionStorageAreas
): boolean {
  if (!accessToken || !Number.isFinite(validTo) || validTo <= Date.now()) {
    return false;
  }

  let areas: SessionStorageAreas;

  try {
    areas = storageAreas ?? {
      localStorage: window.localStorage,
      sessionStorage: window.sessionStorage,
    };
  } catch {
    return false;
  }

  const serializedSession = serializeStoredSession(createStoredSession(accessToken, validTo));

  try {
    areas.localStorage.removeItem(SESSION_STORAGE_KEY);
    areas.sessionStorage.setItem(SESSION_STORAGE_KEY, serializedSession);

    if (
      areas.localStorage.getItem(SESSION_STORAGE_KEY) === null &&
      areas.sessionStorage.getItem(SESSION_STORAGE_KEY) === serializedSession
    ) {
      return true;
    }
  } catch {
    // Cleanup below. The caller treats failure as a terminal login failure too.
  }

  try {
    areas.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // The caller makes another best-effort cleanup before showing the failure page.
  }

  return false;
}
